import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { brandInfo } from '@/mocks/brand';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const faqData = [
    {
      question: '黄桃视频是否需要付费？',
      answer: '黄桃视频是免费高清在线视频平台，所有影视资源均可免费观看。我们始终坚持免费服务理念，无隐藏收费项目。',
    },
    {
      question: '黄桃视频与其他视频平台（樱花视频、糖豆视频）有什么不同？',
      answer: '黄桃视频在资源丰富度、画质体验和更新速度方面具有明显优势。支持4K超清画质，日更新量超过500集，且拥有更智能的AI推荐算法，能为用户提供更精准的个性化推荐。',
    },
    {
      question: '如何在手机上安装黄桃视频APP？',
      answer: '安卓用户可访问黄桃视频官网下载APK安装包；苹果用户在App Store搜索"黄桃视频"即可下载。安装完成后即可在手机和平板上畅享高清影视内容。',
    },
    {
      question: '黄桃视频支持哪些清晰度？',
      answer: '黄桃视频支持流畅、标清、高清720P、超清1080P和4K超清等多种清晰度。系统会根据您的网络状况自动选择最佳画质，确保流畅播放。',
    },
    {
      question: '视频播放卡顿怎么办？',
      answer: '建议您检查网络连接，或尝试切换至较低清晰度。黄桃视频内置智能码率调节功能，在弱网环境下会自动降低画质以保证流畅播放。',
    },
  ];

  return (
    <>
      <SchemaOrg type="Organization" data={{
        name: brandInfo.brandName,
        url: 'https://huangtao.com',
        description: brandInfo.description,
        slogan: brandInfo.slogan,
        foundingDate: brandInfo.founded,
        email: brandInfo.contact.email,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressCountry: 'CN',
        },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `${t('about.title')} - ${t('site.name')}`,
        description: brandInfo.description,
        url: 'https://huangtao.com/about',
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://huangtao.com' },
          { '@type': 'ListItem', position: 2, name: t('about.title') },
        ],
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-24 pb-12 md:pb-16">
        <Breadcrumb
          items={[
            { label: t('breadcrumb.home'), path: '/' },
            { label: t('about.title') },
          ]}
        />

        <section className="mb-14">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            {t('about.title')}
          </h1>
          <p className="text-foreground-500 text-sm max-w-3xl">
            {t('about.brandIntro')} — {brandInfo.slogan}
          </p>
        </section>

        <section className="mb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-xl font-bold text-foreground-900 mb-4">{t('about.brandIntro')}</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {brandInfo.description}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                {brandInfo.stats.map((stat, i) => (
                  <div key={i} className="text-center p-4 rounded-lg bg-accent-50 border border-accent-100">
                    <p className="text-2xl font-bold font-display text-accent-600">
                      {stat.value}<span className="text-sm">{stat.suffix}</span>
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg overflow-hidden h-72 lg:h-80 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20tech%20company%20office%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20atmosphere%2C%20creative%20workspace%2C%20orange%20and%20charcoal%20color%20scheme%2C%20professional%20yet%20relaxed%20environment%2C%20soft%20natural%20lighting&width=800&height=600&seq=about-img-brand-01&orientation=landscape"
                alt="黄桃视频 品牌形象"
                className="w-full h-full object-cover"
                title="黄桃视频 品牌形象"
              />
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-foreground-900 mb-6">{t('about.features')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brandInfo.features.map((feature, i) => (
              <div
                key={i}
                className="p-5 rounded-lg bg-background-50 border border-background-200/60 hover:border-accent-200 transition-all duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className={`${feature.icon} text-lg`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">{feature.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-foreground-900 mb-6">{t('about.milestones')}</h2>
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-accent-200 hidden sm:block"></div>
            <div className="flex flex-col gap-6">
              {brandInfo.milestones.map((milestone, i) => (
                <div key={i} className="flex gap-4 sm:gap-6">
                  <div className="hidden sm:flex w-8 h-8 flex-shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold relative z-10">
                    {i + 1}
                  </div>
                  <div className="flex-1 bg-background-50 border border-background-200/60 rounded-lg p-4 sm:p-5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold text-accent-600 bg-accent-50 px-2 py-0.5 rounded">
                        {milestone.year}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground-900">{milestone.title}</h3>
                    </div>
                    <p className="text-xs text-foreground-500">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-foreground-900 mb-6">{t('about.testimonials')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brandInfo.testimonials.map((item, i) => (
              <div
                key={i}
                className="p-5 rounded-lg bg-background-50 border border-background-200/60"
              >
                <div className="flex items-center gap-1 mb-2">
                  {Array.from({ length: item.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  &ldquo;{item.content}&rdquo;
                </p>
                <p className="text-xs font-semibold text-foreground-900">{item.name}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl font-bold text-foreground-900 mb-6">{t('about.contact')}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">{brandInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">微信</p>
                  <p className="text-sm font-medium text-foreground-900">{brandInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">地址</p>
                  <p className="text-sm font-medium text-foreground-900">{brandInfo.contact.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">工作时间</p>
                  <p className="text-sm font-medium text-foreground-900">{brandInfo.contact.businessHours}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden h-64 lg:h-full bg-background-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.9505381227637!2d116.48047361538541!3d39.99241697941716!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f054eb75a5eb3d%3A0xa8baa3d3d1652d9!2z5YyX5Lqs5biC5pyd6Ziz5Yy65pyb5YyX5biC5LiN6Lev!5e0!3m2!1szh-CN!2scn!4v1690000000000!5m2!1szh-CN!2scn"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="黄桃视频 地址"
                aria-label="黄桃视频公司地址地图"
              ></iframe>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold text-foreground-900 mb-6">常见问题</h2>
          <div className="flex flex-col gap-3">
            {faqData.map((faq, i) => (
              <details key={i} className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-50/80 transition-colors">
                  <span className="text-sm font-medium text-foreground-900 pr-6">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}